<template>
  <div class="input-keyword-place" v-bind:class="{ inline }">
    <place-keyword-target-selector
      v-bind:selectType="keywordTargetSelectType"
      v-bind:selectedKeywordTarget="keywordTargets"
      v-bind:selectedAllKeywordTargetLabel="selectedAllKeywordTargetLabel"
      v-bind:idPrefix="idPrefixForKeywordTargetSelector"
      v-bind:onApiError="handleApiError"
      v-on:place-keyword-target-changed="
        $emit('keyword-target-changed', $event)
      "
    />
    <input-keyword
      v-model="localValue"
      v-bind:placeholder="placeholder"
      v-on:search="$emit('keyword-changed', $event)"
    />
  </div>
</template>

<script>
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

/**
 * プレイス検索フォームコンポーネント
 */
export default {
  name: 'AppInputKeywordPlace',
  props: {
    /**
     * フォームを横並びに表示する場合はtrue
     */
    inline: {
      type: Boolean,
      default: false,
    },
    /**
     * 検索項目コンポーネントのタイプ（0:単一選択, 1:複数選択）
     */
    keywordTargetSelectType: {
      type: Number,
      default: 1,
    },
    /**
     * DOMのidのプレフィックス
     * 1画面に本コンポーネントを複数配置する場合は設定が必要です
     */
    idPrefix: {
      type: String,
      default: '',
    },
    /**
     * プレイスホルダーに表示する文字列
     */
    placeholder: {
      type: String,
      default: null,
    },
    /**
     * 「すべての検索項目」を選択している時に表示されるラベル
     */
    allKeywordTargetLabel: {
      type: String,
      default: null,
    },
    /**
     * 検索キーワード
     */
    value: {
      type: String,
      default: '',
    },
    /**
     * 選択されている検索項目
     */
    keywordTargets: {
      type: Array,
      default: function () {
        return []
      },
    },
  },
  emits: [
    /**
     * キーワード入力イベント
     * @param {String} value 入力値
     */
    'input',
    /**
     * キーワード変更イベント
     * @param {String} keyword キーワード
     */
    'keyword-changed',
    /**
     * 検索項目変更イベント
     * @param {String[]} selectedKeywordTarget 選択された検索項目
     */
    'keyword-target-changed',
  ],
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  computed: {
    // v-modelネスト用
    localValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    /**
     * 検索項目コンポーネントのエレメントID
     * @type {String}
     */
    idPrefixForKeywordTargetSelector: function () {
      const base = 'place-keyword-target-selector'
      return this.idPrefix ? `${this.idPrefix}-${base}` : base
    },
    /**
     * 「すべての検索項目」を選択している時に表示されるラベル
     * @type {String}
     */
    selectedAllKeywordTargetLabel: function () {
      return (
        this.allKeywordTargetLabel ||
        this.$t('appInputKeywordPlace.allKeywordTargets')
      )
    },
  },
  mounted: function () {
    this.$log.debug('AppInputKeywordPlace mounted')
  },
}
</script>

<style lang="scss" scoped>
.input-keyword-place {
  width: 100%;

  &:not(.inline) {
    .place-keyword-target-selector {
      ::v-deep .form-control {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        &:focus {
          /**
          * focus時のbox-shadowが隠れるのを回避
          * z-indexは検索ボタン（z-index: 2;）より大きく設定
          */
          position: relative;
          z-index: 3;
        }
      }
    }

    .input-keyword {
      width: 100%;

      ::v-deep .keyword-search {
        border-top-left-radius: 0;
      }

      ::v-deep .input-group-append {
        .keyword-search-button {
          border-top-right-radius: 0;
        }
      }
    }
  }

  &.inline {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;

    .place-keyword-target-selector {
      ::v-deep .form-control {
        border-right: none;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;

        &:focus {
          /**
          * focus時のbox-shadowが隠れるのを回避
          * z-indexは検索ボタン（z-index: 2;）より大きく設定
          */
          position: relative;
          z-index: 3;
        }
      }
    }

    .input-keyword {
      width: 100%;

      ::v-deep .keyword-search {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
}
</style>
